<template>
  <div class="tw-bg-white tw-rounded-xl tw-shadow-sm tw-flex-shrink-0">
    <!-- Header (clickable to expand/collapse) -->
    <button
      class="tw-w-full tw-flex tw-items-center tw-justify-between tw-px-4 tw-py-3 tw-text-left hover:tw-bg-gray-50 tw-rounded-t-xl tw-transition-colors"
      @click="expanded = !expanded"
    >
      <div class="tw-flex tw-items-center tw-gap-2">
        <svg
          class="tw-w-4 tw-h-4 tw-text-gray-400 tw-transition-transform"
          :class="expanded ? 'tw-rotate-180' : ''"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M19 9l-7 7-7-7"
          />
        </svg>
        <h3 class="tw-font-semibold tw-text-xs tw-text-gray-500 tw-tracking-wider tw-uppercase">
          Activiteitenlog
        </h3>
        <span
          v-if="events.length > 0"
          class="tw-text-xs tw-bg-gray-100 tw-text-gray-500 tw-rounded-full tw-px-2 tw-py-0.5 tw-font-medium"
          >{{ events.length }}</span
        >
        <span
          v-if="isFetchingLogs || isFetchingTickets"
          class="tw-w-3 tw-h-3 tw-border-2 tw-border-gray-300 tw-border-t-blue-400 tw-rounded-full tw-animate-spin tw-flex-shrink-0"
        />
      </div>
      <span
        v-if="legacyAddress"
        @click.stop="openModal"
        class="tw-text-xs tw-flex tw-items-center tw-gap-1 tw-px-3 tw-py-1.5 tw-rounded-lg tw-bg-blue-600 tw-text-white hover:tw-bg-blue-700 tw-font-medium tw-transition-colors"
      >
        <plus-icon class="tw-w-3.5 tw-h-3.5" />
        Notitie toevoegen
      </span>
    </button>

    <!-- Timeline -->
    <div v-if="expanded" class="tw-max-h-80 tw-overflow-y-auto tw-border-t tw-border-gray-100">
      <div
        v-if="events.length === 0 && !isFetchingLogs && !isFetchingTickets"
        class="tw-px-4 tw-py-8 tw-text-sm tw-text-gray-400 tw-text-center"
      >
        Geen activiteit gevonden voor dit adres.
      </div>

      <div
        v-for="event in events"
        :key="event._key"
        class="tw-flex tw-gap-3 tw-px-4 tw-py-3 tw-border-b tw-border-gray-50 last:tw-border-b-0 hover:tw-bg-gray-50/50 tw-transition-colors"
      >
        <!-- Icon -->
        <div class="tw-flex-shrink-0 tw-pt-0.5">
          <div
            v-if="event._type === 'action'"
            class="tw-w-7 tw-h-7 tw-rounded-full tw-flex tw-items-center tw-justify-center"
            :class="contextIconClass(event.context)"
          >
            <phone-icon v-if="event.context === 'Phone'" class="tw-w-3.5 tw-h-3.5 tw-text-white" />
            <mail-icon
              v-else-if="event.context === 'Email'"
              class="tw-w-3.5 tw-h-3.5 tw-text-white"
            />
            <office-building-icon
              v-else-if="event.context === 'Internal'"
              class="tw-w-3.5 tw-h-3.5 tw-text-white"
            />
            <home-icon v-else class="tw-w-3.5 tw-h-3.5 tw-text-white" />
          </div>
          <div
            v-else
            class="tw-w-7 tw-h-7 tw-rounded-full tw-flex tw-items-center tw-justify-center"
            :class="ticketIconClass(event.type)"
          >
            <clipboard-check-icon class="tw-w-3.5 tw-h-3.5 tw-text-white" />
          </div>
        </div>

        <!-- Content -->
        <div class="tw-flex-1 tw-min-w-0">
          <!-- Action log -->
          <template v-if="event._type === 'action'">
            <div class="tw-flex tw-items-center tw-justify-between tw-gap-2">
              <div class="tw-flex tw-items-center tw-gap-1.5 tw-flex-wrap">
                <span class="tw-text-xs tw-font-semibold tw-text-gray-700">{{
                  contextLabel(event.context)
                }}</span>
                <span v-if="event.subject" class="tw-text-xs tw-text-gray-400"
                  >· {{ event.subject }}</span
                >
                <span
                  class="tw-text-xs tw-px-1.5 tw-py-0.5 tw-rounded-full tw-font-medium"
                  :class="
                    event.is_inbound
                      ? 'tw-bg-blue-50 tw-text-blue-600'
                      : 'tw-bg-emerald-50 tw-text-emerald-600'
                  "
                >
                  {{ event.is_inbound ? 'Inkomend' : 'Uitgaand' }}
                </span>
                <span
                  v-if="event.type === 'Complaint'"
                  class="tw-text-xs tw-px-1.5 tw-py-0.5 tw-rounded-full tw-bg-red-50 tw-text-red-600 tw-font-medium"
                  >Klacht</span
                >
              </div>
              <span class="tw-text-xs tw-text-gray-400 tw-whitespace-nowrap tw-flex-shrink-0">
                {{ formatDate(event.created_at) }}
              </span>
            </div>
            <p class="tw-text-sm tw-text-gray-800 tw-leading-snug tw-mt-1">{{ event.value }}</p>
            <span class="tw-text-xs tw-text-gray-400 tw-mt-0.5 tw-block">
              {{ formatCreatedBy(event.created_by) }}
            </span>
          </template>

          <!-- Ticket -->
          <template v-else>
            <div class="tw-flex tw-items-center tw-justify-between tw-gap-2">
              <div class="tw-flex tw-items-center tw-gap-1.5 tw-flex-wrap">
                <span
                  class="tw-text-xs tw-font-medium tw-px-1.5 tw-py-0.5 tw-rounded"
                  :class="ticketTypeBadgeClass[event.type] || 'tw-bg-gray-100 tw-text-gray-600'"
                >
                  {{ ticketTypeLabels[event.type] || event.type }}
                </span>
                <span class="tw-text-xs tw-text-gray-500">{{ event.partner }}</span>
                <span
                  class="tw-text-xs tw-font-medium"
                  :class="ticketStatusTextClass[event.status]"
                >
                  {{ ticketStatusLabels[event.status] || event.status }}
                </span>
              </div>
              <span class="tw-text-xs tw-text-gray-400 tw-whitespace-nowrap tw-flex-shrink-0">
                {{ formatDate(event.created_at) }}
              </span>
            </div>
            <div class="tw-flex tw-items-center tw-gap-3 tw-mt-1">
              <span v-if="event.appointment_date" class="tw-text-xs tw-text-gray-500">
                Afspraak: {{ formatShortDate(event.appointment_date) }}
              </span>
              <!-- TODO: replace hardcoded URL with ticketsBaseUrl from '@/logic/common/urls' -->
              <a
                :href="`https://tickets.norm.eu/v2/tickets/${event.id}`"
                target="_blank"
                class="tw-text-xs tw-text-blue-500 hover:tw-text-blue-700 hover:tw-underline tw-flex tw-items-center tw-gap-0.5 tw-no-underline"
              >
                #{{ event.id }}
                <external-link-icon class="tw-w-3 tw-h-3" />
              </a>
            </div>
          </template>
        </div>
      </div>
    </div>

    <!-- Add note modal -->
    <GenericModal
      v-if="showModal"
      title="Notitie toevoegen"
      width="480px"
      :disabled="!newLog.value.trim() || isSubmitting"
      @close="closeModal"
      @submit="submitLog"
    >
      <div class="tw-flex tw-flex-col tw-gap-4">
        <div class="tw-flex tw-gap-3">
          <div class="tw-flex-1">
            <label class="tw-text-xs tw-font-medium tw-text-gray-600 tw-mb-1 tw-block">Type</label>
            <select
              v-model="newLog.type"
              class="tw-w-full tw-border tw-border-gray-300 tw-rounded-lg tw-px-3 tw-py-2 tw-text-sm focus:tw-outline-none focus:tw-border-blue-400"
            >
              <option value="Comment">Opmerking</option>
              <option value="Complaint">Klacht</option>
            </select>
          </div>
          <div class="tw-flex-1">
            <label class="tw-text-xs tw-font-medium tw-text-gray-600 tw-mb-1 tw-block"
              >Kanaal</label
            >
            <select
              v-model="newLog.context"
              class="tw-w-full tw-border tw-border-gray-300 tw-rounded-lg tw-px-3 tw-py-2 tw-text-sm focus:tw-outline-none focus:tw-border-blue-400"
            >
              <option value="Phone">Telefonisch</option>
              <option value="Email">E-mail</option>
              <option value="Internal">Intern</option>
              <option value="D2D">D2D</option>
            </select>
          </div>
        </div>

        <div>
          <label class="tw-text-xs tw-font-medium tw-text-gray-600 tw-mb-1 tw-block"
            >Onderwerp</label
          >
          <select
            v-model="newLog.subject"
            class="tw-w-full tw-border tw-border-gray-300 tw-rounded-lg tw-px-3 tw-py-2 tw-text-sm focus:tw-outline-none focus:tw-border-blue-400"
          >
            <option value="">— Selecteer onderwerp —</option>
            <option v-for="s in subjects" :key="s" :value="s">{{ s }}</option>
          </select>
        </div>

        <div>
          <label class="tw-text-xs tw-font-medium tw-text-gray-600 tw-mb-1 tw-block"
            >Beschrijving *</label
          >
          <textarea
            v-model="newLog.value"
            rows="3"
            placeholder="Voer een beschrijving in..."
            class="tw-w-full tw-border tw-border-gray-300 tw-rounded-lg tw-px-3 tw-py-2 tw-text-sm tw-resize-none focus:tw-outline-none focus:tw-border-blue-400"
          />
        </div>

        <div
          v-if="submitError"
          class="tw-text-xs tw-text-red-500 tw-bg-red-50 tw-rounded tw-px-3 tw-py-2"
        >
          {{ submitError }}
        </div>
      </div>
    </GenericModal>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue'
import { useQuery, useQueryClient } from '@tanstack/vue-query'
import fetchData from '@/util/fetch'
import store from '@/store'
import { useAddressLocation } from './composables/useAddressLocation'
import GenericModal from '@/components/shared/GenericModal.vue'
import {
  PhoneIcon,
  MailIcon,
  OfficeBuildingIcon,
  HomeIcon,
  ClipboardCheckIcon,
  PlusIcon,
  ExternalLinkIcon,
} from '@vue-hero-icons/outline'

const props = defineProps({
  meterDetail: { type: Object, default: null },
})

const expanded = ref(false)
const showModal = ref(false)
const newLog = ref({ type: 'Comment', context: 'Internal', subject: 'Onderhoud', value: '' })
const isSubmitting = ref(false)
const submitError = ref('')

const queryClient = useQueryClient()

// ─── Location / legacy address ────────────────────────────────────────────────
const { legacyAddress, woco } = useAddressLocation(computed(() => props.meterDetail))
const currentUser = computed(() => store.getters['auth/getUser'] ?? '')

// ─── Subjects dropdown ────────────────────────────────────────────────────────
const { data: subjectsData } = useQuery({
  queryKey: ['actionSubjects'],
  queryFn: () => fetchData('/api/v1/actions/subjects').then((r) => r?.data ?? []),
  refetchOnWindowFocus: false,
  staleTime: 10 * 60 * 1000,
})

const subjects = computed(() => {
  const raw = subjectsData.value
  const list = raw?.subjects ?? (Array.isArray(raw) ? raw : [])
  if (!Array.isArray(list)) return []
  return list
    .map((s) => (typeof s === 'string' ? s : s?.name))
    .filter((s) => s && s !== 'OLD' && s !== '')
})

// ─── Call center logs ─────────────────────────────────────────────────────────
const { data: logsData, isFetching: isFetchingLogs } = useQuery({
  queryKey: computed(() => ['addressLogs', legacyAddress.value]),
  queryFn: () =>
    fetchData(`/api/v1/actions/address/${legacyAddress.value}`).then((r) => r?.data?.actions ?? []),
  enabled: computed(() => !!legacyAddress.value),
  refetchOnWindowFocus: false,
})

// ─── Tickets ──────────────────────────────────────────────────────────────────
const ticketQuery = computed(() => {
  if (!props.meterDetail) return null
  const { zip_code, zip_code_ext, house_number } = props.meterDetail
  if (!zip_code || !house_number) return null
  return `${String(house_number).replace(/\s+/g, '')}${zip_code}${zip_code_ext || ''}`.toLowerCase()
})

const { data: ticketsData, isFetching: isFetchingTickets } = useQuery({
  queryKey: computed(() => ['addressTickets', ticketQuery.value]),
  queryFn: () =>
    fetchData(
      `/tickets/api/v1/tickets?view=list&query=${encodeURIComponent(ticketQuery.value)}`
    ).then((r) => r?.data ?? []),
  enabled: computed(() => !!ticketQuery.value),
  refetchOnWindowFocus: false,
})

// ─── Unified chronological event list ────────────────────────────────────────
const events = computed(() => {
  const actionEvents = (logsData.value ?? []).map((a) => ({
    ...a,
    _type: 'action',
    _key: `action-${a.id}`,
    _date: new Date(a.created_at),
  }))
  const ticketEvents = (ticketsData.value ?? []).map((t) => ({
    ...t,
    _type: 'ticket',
    _key: `ticket-${t.id}`,
    _date: new Date(t.created_at),
  }))
  return [...actionEvents, ...ticketEvents].sort((a, b) => b._date - a._date)
})

// ─── Icon styling ─────────────────────────────────────────────────────────────
const contextIconClass = (ctx) =>
  ({
    Phone: 'tw-bg-blue-500',
    Email: 'tw-bg-amber-500',
    Internal: 'tw-bg-gray-500',
    D2D: 'tw-bg-emerald-500',
  }[ctx] || 'tw-bg-gray-400')

const contextLabel = (ctx) =>
  ({
    Phone: 'Telefonisch',
    Email: 'E-mail',
    Internal: 'Intern',
    D2D: 'Huisbezoek',
  }[ctx] || ctx)

const ticketIconClass = (type) =>
  ({
    installation: 'tw-bg-blue-500',
    inspection: 'tw-bg-purple-500',
    maintenance: 'tw-bg-orange-500',
  }[type] || 'tw-bg-gray-400')

// ─── Ticket labels / styling ──────────────────────────────────────────────────
const ticketTypeLabels = {
  installation: 'Installatie',
  inspection: 'Schouw',
  maintenance: 'Onderhoud',
}
const ticketTypeBadgeClass = {
  installation: 'tw-bg-blue-100 tw-text-blue-700',
  inspection: 'tw-bg-purple-100 tw-text-purple-700',
  maintenance: 'tw-bg-orange-100 tw-text-orange-700',
}
const ticketStatusLabels = {
  created: 'Te plannen',
  planned: 'Gepland',
  ongoing: 'Bezig',
  finished: 'Afgerond',
  partner_approved: 'Goedgekeurd',
  rejected: 'Afgekeurd',
  approved: 'NORM GG',
  cancelled: 'Geannuleerd',
}
const ticketStatusTextClass = {
  created: 'tw-text-yellow-600',
  planned: 'tw-text-gray-600',
  ongoing: 'tw-text-amber-600',
  finished: 'tw-text-blue-600',
  partner_approved: 'tw-text-green-600',
  approved: 'tw-text-green-700',
  rejected: 'tw-text-red-600',
  cancelled: 'tw-text-gray-400',
}

// ─── Modal / submit ───────────────────────────────────────────────────────────
const openModal = () => {
  newLog.value = { type: 'Comment', context: 'Internal', subject: 'Onderhoud', value: '' }
  submitError.value = ''
  showModal.value = true
}

const closeModal = () => {
  showModal.value = false
}

const submitLog = async () => {
  if (!newLog.value.value.trim() || !legacyAddress.value) return
  isSubmitting.value = true
  submitError.value = ''
  try {
    const response = await fetchData('/api/v1/actions', {
      method: 'POST',
      body: JSON.stringify({
        address: legacyAddress.value,
        type: newLog.value.type,
        context: newLog.value.context,
        subject: newLog.value.subject,
        value: newLog.value.value.trim(),
        is_inbound: true,
        woco: woco.value,
        created_by: currentUser.value,
        has_follow_up: false,
        is_resolved: false,
      }),
    })
    if (response?.status === 'error') {
      throw new Error(response.message || 'Server error')
    }
    showModal.value = false
    queryClient.invalidateQueries(['addressLogs'])
  } catch (e) {
    submitError.value = 'Opslaan mislukt. Probeer het opnieuw.'
    console.error('Failed to create action log:', e)
  } finally {
    isSubmitting.value = false
  }
}

// ─── Formatting ───────────────────────────────────────────────────────────────
const formatDate = (d) =>
  d
    ? new Date(d).toLocaleDateString('nl-NL', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    : '—'

const formatShortDate = (d) =>
  d
    ? new Date(d).toLocaleDateString('nl-NL', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
      })
    : '—'

const formatCreatedBy = (email) => {
  if (!email) return ''
  const atIdx = email.indexOf('@')
  return atIdx > 0 ? email.substring(0, atIdx) : email
}
</script>
